// src/utils/api.js
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";

// Mensajes “humanos” por status
function friendlyByStatus(status) {
  if (status === 400) return "Hay datos inválidos. Revisa la información e intenta de nuevo.";
  if (status === 401) return "Tu sesión expiró. Vuelve a iniciar sesión.";
  if (status === 403) return "No tienes permisos para realizar esta acción.";
  if (status === 404) return "No se encontró lo solicitado.";
  if (status >= 500) return "El servidor tuvo un problema. Intenta más tarde.";
  return "No se pudo completar la acción.";
}

function isNetworkError(err) {
  const msg = String(err?.message || "");
  return msg.includes("Failed to fetch") || msg.includes("NetworkError");
}

function friendlyNetworkMessage() {
  return "No se pudo conectar con el servidor. Revisa tu conexión o que el sistema esté disponible.";
}

async function readBody(res) {
  const ct = res.headers.get("content-type") || "";
  try {
    if (ct.includes("application/json")) return await res.json();
    return await res.text();
  } catch {
    return null;
  }
}

function pickServerMessage(body) {
  if (!body) return "";
  if (typeof body === "string") return body;
  return body.error || body.message || "";
}

// Error “tipado” para poder decidir qué hacer en UI
export class ApiError extends Error {
  constructor({ status, userMessage, serverMessage, body }) {
    super(userMessage);
    this.name = "ApiError";
    this.status = status;
    this.userMessage = userMessage;
    this.serverMessage = serverMessage;
    this.body = body;
  }
}

/**
 * apiFetch:
 * - Maneja JSON y FormData
 * - Maneja 401/403/400/500 con mensajes amigables
 * - Opcional: Authorization Bearer si existe token
 * - Optional: showError (default true) -> muestra Swal automáticamente
 */
export async function apiFetch(path, options = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    showError = true,
    withAuth = true,
    credentials = "include"
  } = options;

  const token = withAuth ? localStorage.getItem("access_token") : null;

  const finalHeaders = { ...headers };

  // Si es JSON (no FormData), ponemos content-type
  const isFormData = body instanceof FormData;
  if (!isFormData && body != null && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  if (token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: isFormData ? body : body != null ? JSON.stringify(body) : undefined,
      credentials
    });

    if (!res.ok) {
      const parsed = await readBody(res);
      const serverMessage = pickServerMessage(parsed);
      const userMessage = serverMessage || friendlyByStatus(res.status);

      const err = new ApiError({
        status: res.status,
        userMessage,
        serverMessage,
        body: parsed
      });

      // Log técnico para ti
      console.error(`[API ERROR] ${method} ${path}`, {
        status: res.status,
        body: parsed
      });

      // UI amigable
      if (showError) {
        // 401: opcional mandar a login
        if (res.status === 401) {
          await Swal.fire("Sesión expirada", userMessage, "warning");
          // opcional:
          // localStorage.removeItem("access_token");
          // window.location.href = "/login";
        } else {
          await Swal.fire("Error", userMessage, "error");
        }
      }

      throw err;
    }

    // OK: devolver JSON o texto
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    return res.text();
  } catch (err) {
    // Network / CORS / server down
    if (isNetworkError(err)) {
      console.error(`[NETWORK ERROR] ${method} ${path}`, err);
      if (showError) await Swal.fire("Error", friendlyNetworkMessage(), "error");
      throw new Error(friendlyNetworkMessage());
    }

    // otros errores
    throw err;
  }
}
